import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, homeFor } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import { Spinner } from "@/components/Shell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import PaymentResult from "@/pages/PaymentResult";
import PlatformApp from "@/pages/platform/PlatformApp";
import TenantApp from "@/pages/tenant/TenantApp";
import MemberApp from "@/pages/portal/MemberApp";

const ProtectedRoute = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const effectiveUser = user || location.state?.user;
  if (loading && !effectiveUser) return <Spinner />;
  if (!effectiveUser) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(effectiveUser.role)) return <Navigate to={homeFor(effectiveUser)} replace />;
  return children;
};

const RoleHome = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const effectiveUser = user || location.state?.user;
  if (loading && !effectiveUser) return <Spinner />;
  if (!effectiveUser) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(effectiveUser)} replace />;
};

function AppRouter() {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<RoleHome />} />
      <Route path="/onboarding" element={<ProtectedRoute>{<Onboarding />}</ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute roles={["tenant_admin"]}>{<PaymentResult success />}</ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute roles={["tenant_admin"]}>{<PaymentResult />}</ProtectedRoute>} />
      <Route path="/platform/*" element={<ProtectedRoute roles={["super_admin"]}>{<PlatformApp />}</ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute roles={["tenant_admin"]}>{<TenantApp />}</ProtectedRoute>} />
      <Route path="/portal/*" element={<ProtectedRoute roles={["member"]}>{<MemberApp />}</ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
